import { useEffect, useState } from "react"
import { Download } from "lucide-react"
import Card from "../components/Card"
import StatusPill from "../components/StatusPill"

type ReportItem = {
  label: string
  value: string
  delta: string
}

type Transaction = {
  date: string
  description: string
  amount: number
  status: string
  source: string
}

const ReportsPage = () => {
  const [reports, setReports] = useState<ReportItem[]>([])
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [dateRange, setDateRange] = useState("this_month")

  useEffect(() => {
    fetch(`/api/reports?range=${dateRange}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.reports) {
          setReports(data.reports)
        }
        if (data.exportData) {
          setTransactions(data.exportData)
        }
      })
      .finally(() => setLoading(false))
  }, [dateRange])

  const handleDateRangeChange = (value: string) => {
    setLoading(true)
    setDateRange(value)
  }

  const handleExport = () => {
    if (transactions.length === 0) {
      alert("No data to export for this range")
      return
    }

    const csvContent = "data:text/csv;charset=utf-8," 
      + "Date,Description,Amount,Status,Source\n"
      + transactions.map(t => `${t.date},"${t.description}",${t.amount},${t.status},${t.source}`).join("\n")
    
    const encodedUri = encodeURI(csvContent)
    const link = document.createElement("a")
    link.setAttribute("href", encodedUri)
    link.setAttribute("download", `agency_report_${dateRange}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-lg font-semibold text-slate-900">Agency Reports</p>
            <p className="text-sm text-stone">
              Profitability, cost, and revenue analysis.
            </p>
          </div>
          <div className="flex gap-2">
            <select
                value={dateRange}
                onChange={(e) => handleDateRangeChange(e.target.value)}
                className="rounded-full bg-slate-50 border border-line px-4 py-2 text-sm font-semibold text-slate-700 outline-none focus:border-moss"
            >
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="this_quarter">This Quarter</option>
                <option value="ytd">Year to Date</option>
            </select>
            <button
                type="button"
                onClick={handleExport}
                className="flex items-center gap-2 rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200 transition"
            >
                <Download className="h-4 w-4" />
                Export
            </button>
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="flex h-96 items-center justify-center">
          <p className="text-sm text-stone">Generating reports...</p>
        </div>
      ) : reports.length === 0 ? (
        <Card>
          <div className="py-12 text-center">
            <p className="text-sm text-stone">No report data available for this period</p>
            <p className="mt-1 text-xs text-stone">Try selecting a different date range</p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {reports.map((report) => (
            <Card key={report.label}>
              <p className="text-sm text-stone">{report.label}</p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">{report.value}</p>
              <StatusPill tone="neutral" label={report.delta} className="mt-3" />
              </Card>
            ))}
          </div>
      )}
    </div>
  )
}

export default ReportsPage
